import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../Components/ui/Button';
import FreeConsultationCTA from '../Components/FreeConsultationCTA';
import Booking from '../Components/Booking/Booking';
import { listServices } from '../services/serviceService';
import SEO from '../Components/shared/SEO';

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardItem = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

const MassageTherapy = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listServices().then(setServices).finally(() => setLoading(false));
  }, []);

  const jsonLd = services.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((s, i) => ({
      '@type': 'Service',
      position: i + 1,
      name: s.name,
      description: s.description || undefined,
      areaServed: 'Nigeria',
      offers: {
        '@type': 'Offer',
        price: s.price,
        priceCurrency: 'NGN'
      }
    }))
  } : undefined;

  return (
    <div className="min-h-screen">
      <SEO
        title="Massage Therapy Services & Pricing"
        path="/massage-therapy"
        description="Browse Serenoly's massage therapy menu — Swedish, deep tissue, hot stone, sports, prenatal massage, reflexology and more, delivered by verified therapists across Nigeria."
        jsonLd={jsonLd}
      />
      {/* Header section */}
      <div className="relative h-[300px] w-full">
        <div className="absolute inset-0">
          <img
            src={'/static/images/pagesBg/massage.jpg'}
            alt="Spa Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center">
          <h1 className="text-4xl md:text-5xl text-white text-center font-semibold">
            Massage <br />
            Therapy
          </h1>
        </div>
      </div>

      {/* Services section */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-3xl font-bold text-center text-dim-light mb-12 font-lato">
          Massage Therapy Services
        </h2>

        {!loading && services.length === 0 && (
          <p className="text-center text-gray-500 mb-12">
            Our service menu is being updated — check back shortly, or book directly and we'll confirm pricing with you.
          </p>
        )}

        <motion.div
          className="flex overflow-x-auto md:overflow-visible gap-4 md:flex-wrap px-1 -mx-2 md:mx-0"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{
            scrollSnapType: 'x mandatory',
          }}
        >
          {services.map((treatment, i) => (
            <motion.div
              key={treatment._id}
              className="flex-none snap-start md:snap-none w-[85%] sm:w-[65%] md:w-[31%] bg-white rounded-xl shadow-md border border-gray-200 p-6"
              variants={cardItem}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <h4 className="text-xl font-semibold text-primary-dark mb-2">
                {treatment.name}
              </h4>
              {treatment.description && (
                <p className="text-gray-600 mb-3">{treatment.description}</p>
              )}
              <p className="font-medium mb-1">Price: ₦{treatment.price.toLocaleString()}</p>
              <p className="font-medium mb-4">Duration: {treatment.durationMinutes} mins</p>
              <Button to="../book-massage" variant="accent">
                Book Now
              </Button>
            </motion.div>
          ))}
        </motion.div>

        <Booking />

        <FreeConsultationCTA message="I'm interested in a free massage consultation. Can you help me?" />
      </div>
    </div>
  );
};

export default MassageTherapy;
